import { isStreamrTvApiMessage, STREAMR_TV_API_MESSAGE_MAGIC_COOKIE, } from '@streamr/tv-api/internals';
import { toUserId } from '@streamr/utils';
import { EventEmitter } from 'eventemitter3';
import pull from 'lodash/pull';
import { hiddenFrameClassName } from '../../components/shared/hiddenStyles';
import { BroadcastStateService } from '../broadcaststate/BroadcastStateService';
import { PurchaseService } from '../purchase/PurchaseService';
export class PluginApiService extends EventEmitter {
    static instance;
    async init() {
        // No initialization needed
    }
    static getInstance() {
        if (!PluginApiService.instance) {
            PluginApiService.instance = new PluginApiService();
        }
        return PluginApiService.instance;
    }
    static getNotificationInterface() {
        return PluginApiService.getInstance();
    }
    // ---- Singleton implementation end
    mountedFramesByPluginId = new Map();
    streamrWrapper;
    subscriptions = new Map();
    videoFrameHandlers = [];
    frameIdCounter = 0;
    processingState = new Map();
    constructor() {
        super();
        window.addEventListener('message', (event) => {
            const { data: apiMessage } = event;
            if (!event.source || !isStreamrTvApiMessage(apiMessage)) {
                return;
            }
            console.warn('got message event magic cookie and source', event);
            const senderFrame = this.findSenderFrameByMessageSource(event.source);
            console.log('senderFrame', senderFrame);
            if (senderFrame?.iframe) {
                console.log('calling onMessageFromPlugin', senderFrame.frame.id, senderFrame.iframe);
                this.onMessageFromPlugin(senderFrame, apiMessage);
            }
        });
        PurchaseService.getInstance().on('purchaseCompleted', (purchase) => {
            // biome-ignore lint/suspicious/noExplicitAny: Type cannot be known here
            const messageToDeliver = {
                magicCookie: STREAMR_TV_API_MESSAGE_MAGIC_COOKIE,
                operation: 'purchaseCompleted',
                purchase,
            };
            const targetFrames = [
                ...this.mountedFramesByPluginId.values(),
            ].flat();
            for (const targetFrame of targetFrames) {
                targetFrame.iframe.contentWindow?.postMessage(messageToDeliver, '*');
            }
        });
    }
    addSubscription(topic, subscriberIframe, publisherPluginId, publisherFrameId) {
        if (!this.subscriptions.has(topic)) {
            this.subscriptions.set(topic, []);
        }
        const subscribers = this.subscriptions.get(topic);
        if (subscribers) {
            // Check if this iframe already has the exact same subscription
            const existingSubscription = subscribers.find((sub) => sub.subscriberIframe === subscriberIframe &&
                sub.publisherPluginId === publisherPluginId &&
                sub.publisherFrameId === publisherFrameId);
            if (existingSubscription) {
                return;
            }
            subscribers.push({
                subscriberIframe,
                publisherPluginId,
                publisherFrameId,
            });
        }
    }
    removeSubscription(topic, subscriberIframe, publisherPluginId, publisherFrameId) {
        const subscribers = this.subscriptions.get(topic);
        if (!subscribers) {
            return;
        }
        const index = subscribers.findIndex((sub) => sub.subscriberIframe === subscriberIframe &&
            sub.publisherPluginId === publisherPluginId &&
            sub.publisherFrameId === publisherFrameId);
        if (index !== -1) {
            subscribers.splice(index, 1);
            // Clean up empty topic arrays
            if (subscribers.length === 0) {
                this.subscriptions.delete(topic);
            }
        }
    }
    onMessageFromPlugin(senderFrame, 
    // biome-ignore lint/suspicious/noExplicitAny: Type cannot be known here
    apiMessage) {
        console.log('onMessageFromPlugin', apiMessage);
        switch (apiMessage.operation) {
            case 'publishNetworkMessage': {
                const protobufMessage = {
                    pluginId: senderFrame.frame.pluginId,
                    data: apiMessage.payload,
                };
                this.streamrWrapper?.publishPluginMessage(protobufMessage);
                break;
            }
            case 'subscribeToPluginMessages': {
                console.log('subscribeToPluginMessages message', apiMessage);
                this.addSubscription(apiMessage.metadata.topic, senderFrame.iframe, apiMessage.metadata.publisherPluginId, apiMessage.metadata.publisherFrameId);
                break;
            }
            case 'unsubscribeFromPluginMessages': {
                console.log('unsubscribeFromPluginMessages message', apiMessage);
                this.removeSubscription(apiMessage.metadata.topic, senderFrame.iframe, apiMessage.metadata.publisherPluginId, apiMessage.metadata.publisherFrameId);
                break;
            }
            case 'publishPluginMessage': {
                const { payload, metadata } = apiMessage;
                const subscriptions = this.subscriptions.get(metadata.topic);
                if (!subscriptions?.length) {
                    console.error(`No subscriptions for topic ${metadata.topic}`);
                    break;
                }
                const publisherPluginId = senderFrame.frame.pluginId;
                const publisherFrameId = senderFrame.frame.id;
                const filteredSubscriptions = subscriptions.filter((sub) => {
                    if (sub.publisherPluginId &&
                        sub.publisherPluginId !== publisherPluginId) {
                        return false;
                    }
                    if (sub.publisherFrameId &&
                        sub.publisherFrameId !== publisherFrameId) {
                        return false;
                    }
                    return true;
                });
                if (!filteredSubscriptions.length) {
                    console.log(`No matching subscriptions after sender filtering for topic ${metadata.topic}`);
                    console.log('senderFrame', senderFrame);
                    console.log('subscriptions', this.subscriptions);
                    break;
                }
                // biome-ignore lint/suspicious/noExplicitAny: Type cannot be known here
                const messageToDeliver = {
                    magicCookie: STREAMR_TV_API_MESSAGE_MAGIC_COOKIE,
                    operation: 'pluginMessageReceived',
                    payload,
                    metadata: {
                        topic: metadata.topic,
                        publisherPluginId,
                        publisherFrameId,
                    },
                };
                filteredSubscriptions.forEach((sub) => sub.subscriberIframe.contentWindow?.postMessage(messageToDeliver, '*'));
                break;
            }
            case 'hideFrame': {
                console.log('hideFrame message', apiMessage);
                this.setFrameVisibility(senderFrame.slotId, senderFrame.keyWithinSlot, true);
                break;
            }
            case 'showFrame': {
                console.log('showFrame message', apiMessage);
                this.setFrameVisibility(senderFrame.slotId, senderFrame.keyWithinSlot, false);
                break;
            }
            case 'getterRequest': {
                const { requestId, methodName } = apiMessage;
                let value;
                // Currently there are no getter methods. When we add them, add the evaluation
                // logic here, like this:
                // if (methodName === 'getFoo') {
                //    value = 'foo'
                //}
                // biome-ignore lint/suspicious/noExplicitAny: Type cannot be known here
                const messageToDeliver = {
                    magicCookie: STREAMR_TV_API_MESSAGE_MAGIC_COOKIE,
                    operation: 'getterResponse',
                    requestId,
                    methodName,
                    value,
                };
                senderFrame.iframe.contentWindow?.postMessage(messageToDeliver, '*');
                break;
            }
            case 'initializationRequest': {
                const activeBroadcast = BroadcastStateService.getInstance().getRequiredActiveBroadcast();
                const pluginContext = {
                    broadcastRole: activeBroadcast.myRole,
                };
                // biome-ignore lint/suspicious/noExplicitAny: Type cannot be known here
                const messageToDeliver = {
                    magicCookie: STREAMR_TV_API_MESSAGE_MAGIC_COOKIE,
                    operation: 'initializationResponse',
                    pluginContext,
                };
                senderFrame.iframe.contentWindow?.postMessage(messageToDeliver, '*');
                break;
            }
            case 'startPurchase': {
                console.log('startPurchase message', apiMessage);
                PurchaseService.getNotificationInterface().startPurchase(apiMessage.metadata);
                break;
            }
            case 'setVideoProcessingHandler': {
                console.log('setVideoProcessingHandler message', apiMessage);
                this.addVideoFrameHandler(senderFrame);
                break;
            }
            case 'reportVideoFrameProcessed': {
                console.log('reportVideoFrameProcessed message', apiMessage);
                const processingState = this.processingState.get(apiMessage.frameId);
                if (processingState === undefined) {
                    throw new Error('Processing state not found');
                }
                if (processingState < this.videoFrameHandlers.length - 1) {
                    this.continueVideoFrameProcessing(processingState, apiMessage.frameId, apiMessage.imageBitmap);
                }
                else {
                    this.completeVideoFrameProcessing(apiMessage.frameId, apiMessage.imageBitmap);
                }
                break;
            }
        }
    }
    continueVideoFrameProcessing(processingState, frameId, imageBitmap) {
        const newProcessingState = processingState + 1;
        this.processingState.set(frameId, newProcessingState);
        const handler = this.videoFrameHandlers[newProcessingState];
        // biome-ignore lint/suspicious/noExplicitAny: Type cannot be known here
        const message = {
            magicCookie: STREAMR_TV_API_MESSAGE_MAGIC_COOKIE,
            operation: 'videoFrame',
            imageBitmap,
            frameId,
        };
        console.log('sending video frame message to iframe', message);
        handler.contentWindow?.postMessage(message, '*', [imageBitmap]);
    }
    completeVideoFrameProcessing(frameId, imageBitmap) {
        const processedImageBitmap = imageBitmap;
        const processedVideoFrame = new VideoFrame(processedImageBitmap, {
            timestamp: 0,
        });
        this.processingState.delete(frameId);
        this.reportVideoFrameProcessed(processedVideoFrame);
    }
    reportVideoFrameProcessed(videoFrame) {
        this.emit('videoFrameProcessed', videoFrame);
    }
    addVideoFrameHandler(senderFrame) {
        this.videoFrameHandlers.push(senderFrame.iframe);
    }
    handleVideoFrame(videoFrame) {
        if (this.videoFrameHandlers.length === 0) {
            this.reportVideoFrameProcessed(videoFrame);
            return;
        }
        const frameId = this.frameIdCounter;
        this.processingState.set(frameId, 0);
        this.frameIdCounter++;
        createImageBitmap(videoFrame).then((imageBitmap) => {
            videoFrame.close();
            const handler = this.videoFrameHandlers[0];
            // biome-ignore lint/suspicious/noExplicitAny: Type cannot be known here
            const message = {
                magicCookie: STREAMR_TV_API_MESSAGE_MAGIC_COOKIE,
                operation: 'videoFrame',
                imageBitmap,
                frameId,
            };
            console.log('sending video frame message to iframe', message);
            handler.contentWindow?.postMessage(message, '*', [imageBitmap]);
        });
    }
    setStreamrWrapper(streamrWrapper) {
        this.streamrWrapper = streamrWrapper;
        this.streamrWrapper.on('pluginMessage', (message, publisherId) => {
            console.log('received plugin message from network', message);
            console.log('mountedFramesByPluginId', this.mountedFramesByPluginId);
            const targetFrames = this.mountedFramesByPluginId.get(message.pluginId);
            if (targetFrames) {
                console.log('sending plugin message to iframes', message);
                // biome-ignore lint/suspicious/noExplicitAny: Type cannot be known here
                const networkMessage = {
                    magicCookie: STREAMR_TV_API_MESSAGE_MAGIC_COOKIE,
                    operation: 'networkMessageReceived',
                    payload: message.data,
                    metadata: {
                        publisherUserId: toUserId(publisherId),
                        publisherBroadcastRole: BroadcastStateService.getInstance().getRequiredActiveBroadcast()
                            .myRole,
                    },
                };
                for (const targetFrame of targetFrames) {
                    targetFrame.iframe.contentWindow?.postMessage(networkMessage, '*');
                }
            }
        });
    }
    findSenderFrameByMessageSource(source) {
        for (const frames of this.mountedFramesByPluginId.values()) {
            for (const frame of frames) {
                if (source === frame.iframe.contentWindow) {
                    return frame;
                }
            }
        }
        return undefined;
    }
    // Notifications to be called by React components
    notifyIframeMounted(slotId, keyWithinSlot, frame, iframe) {
        console.warn('notifyIframeMounted', slotId, keyWithinSlot, frame, iframe);
        console.log('pluginId of mounted frame: ', frame.pluginId);
        if (!this.mountedFramesByPluginId.has(frame.pluginId)) {
            this.mountedFramesByPluginId.set(frame.pluginId, []);
        }
        else {
            // biome-ignore lint/style/noNonNullAssertion: the state has been checked above by this.mountedFramesByPluginId.has()
            const pluginFrames = this.mountedFramesByPluginId.get(frame.pluginId);
            // check if this same frame has already been mounted
            const existingFrame = pluginFrames.find((mountedFrame) => mountedFrame.frame.id === frame.id);
            if (existingFrame !== undefined) {
                // TODO if we implement notifyIframeUnmounted we should add a warning here:
                // e.g. console.warn('Existing duplicate frame removed')
                pull(pluginFrames, existingFrame);
            }
        }
        // biome-ignore lint/style/noNonNullAssertion: the state has been checked above by this.mountedFramesByPluginId.has()
        this.mountedFramesByPluginId.get(frame.pluginId).push({
            frame,
            slotId,
            keyWithinSlot,
            iframe,
        });
    }
    setFrameVisibility(slotId, keyWithinSlot, hide) {
        // Find the iframe element for this slot and key
        for (const frames of this.mountedFramesByPluginId.values()) {
            for (const mountedFrame of frames) {
                if (mountedFrame.slotId === slotId &&
                    mountedFrame.keyWithinSlot === keyWithinSlot) {
                    const iframe = mountedFrame.iframe;
                    if (hide) {
                        iframe.classList.add(hiddenFrameClassName);
                    }
                    else {
                        iframe.classList.remove(hiddenFrameClassName);
                    }
                    console.log(`${hide ? 'Hidden' : 'Shown'} frame ${slotId}/${keyWithinSlot}`, iframe.classList.contains(hiddenFrameClassName));
                    return;
                }
            }
        }
        console.warn(`setFrameVisibility: frame not found ${slotId}/${keyWithinSlot}`);
    }
}
